/**
 * Offline photo upload queue (Jun 2026).
 *
 * Field workers often have no signal on site. When a photo upload fails with
 * a network error, the (already compressed) file is stashed in IndexedDB and
 * automatically retried when connectivity returns — via the 'online' event
 * and a 60s interval wired up in JobsEnhanced.
 */
import axios from 'axios';
import { uploadJobPhotoDirect } from './directUpload';

const DB_NAME = 'ht_photo_queue';
const STORE = 'photos';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
  });
}

export async function enqueuePhoto({ jobId, file }) {
  const db = await openDb();
  const rec = {
    id: (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
    jobId,
    blob: file,
    name: file.name || 'photo.jpg',
    type: file.type || 'image/jpeg',
    queued_at: new Date().toISOString(),
  };
  await tx(db, 'readwrite', (s) => s.put(rec));
  db.close();
  return rec;
}

export async function getQueuedPhotos() {
  const db = await openDb();
  const items = await new Promise((resolve, reject) => {
    const t = db.transaction(STORE, 'readonly');
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return items;
}

export async function removeQueuedPhoto(id) {
  const db = await openDb();
  await tx(db, 'readwrite', (s) => s.delete(id));
  db.close();
}

export async function countQueuedPhotos() {
  try {
    return (await getQueuedPhotos()).length;
  } catch {
    return 0;
  }
}

const isValidationStatus = (st) => st === 400 || st === 403 || st === 404;

let flushing = false;
/**
 * Attempt to upload every queued photo. Validation errors (400/403/404 —
 * e.g. job deleted, photo limit reached) drop the item; network errors keep
 * it for the next attempt. Returns { uploaded, dropped, remaining }.
 */
export async function flushPhotoQueue({ apiBase, token, onUploaded, onDropped }) {
  if (flushing || !navigator.onLine) return { uploaded: 0, dropped: 0, remaining: await countQueuedPhotos() };
  flushing = true;
  let uploaded = 0;
  let dropped = 0;
  try {
    const items = await getQueuedPhotos();
    for (const item of items) {
      const file = new File([item.blob], item.name, { type: item.type });
      try {
        await uploadJobPhotoDirect({ apiBase, jobId: item.jobId, token, file, originalName: item.name });
        await removeQueuedPhoto(item.id);
        uploaded += 1;
      } catch (err) {
        if (isValidationStatus(err?.response?.status)) {
          await removeQueuedPhoto(item.id);
          dropped += 1;
          continue;
        }
        // Direct path unavailable — try the legacy proxied upload.
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`${apiBase}/jobs/${item.jobId}/upload-photo`, fd, {
            headers: token && token !== 'null' ? { Authorization: `Bearer ${token}` } : {},
            timeout: 300000,
          });
          await removeQueuedPhoto(item.id);
          uploaded += 1;
        } catch (err2) {
          if (isValidationStatus(err2?.response?.status)) {
            await removeQueuedPhoto(item.id);
            dropped += 1;
            continue;
          }
          break; // still offline — stop and retry on the next tick
        }
      }
    }
  } catch {
    // IndexedDB unavailable (private mode) — nothing we can do.
  } finally {
    flushing = false;
  }
  if (uploaded && onUploaded) onUploaded(uploaded);
  if (dropped && onDropped) onDropped(dropped);
  return { uploaded, dropped, remaining: await countQueuedPhotos() };
}
